import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useOnlineStatus from '@/hooks/useOnlineStatus';
import ActionButton from '@/components/ActionButton';

interface OfflineFallbackProps {
  /** Fills the viewport. For a global overlay rather than a panel. */
  fullScreen?: boolean;
  /**
   * Sized to sit inside a card or widget body instead of owning the screen.
   * Same illustration and copy, smaller.
   */
  inline?: boolean;
  /**
   * Called once connectivity is back. Without it the page reloads — wrong
   * inside a widget, where refetching the one query is enough.
   */
  onRecover?: () => void;
  /** Set false to keep the screen up until the parent removes it. */
  autoRecover?: boolean;
  /**
   * The request failed but the browser is online, so the copy talks about the
   * request rather than telling the user to check a connection that is fine.
   */
  variant?: 'offline' | 'error';
}

/**
 * Shown when the app cannot reach the API. Port of SP's
 * `components/OfflineFallback`, plus an `inline` size so a dashboard widget can
 * use the same treatment instead of a line of red text.
 */
export default function OfflineFallback({
  fullScreen = false,
  inline = false,
  onRecover,
  autoRecover = true,
  variant = 'offline',
}: OfflineFallbackProps) {
  const { t } = useTranslation();
  const isOnline = useOnlineStatus();
  const [isChecking, setIsChecking] = useState(false);
  const [stillOffline, setStillOffline] = useState(false);

  // If the browser was already online at mount then the API is what is down,
  // and navigator.onLine tells us nothing about recovery — acting on it would
  // reload in a loop.
  const wasOnlineAtMount = useRef(typeof navigator !== 'undefined' ? navigator.onLine : false);
  const prevIsOnlineRef = useRef(isOnline);

  useEffect(() => {
    if (!autoRecover) return;
    const wasOffline = !prevIsOnlineRef.current;
    prevIsOnlineRef.current = isOnline;
    if (isOnline && wasOffline && !wasOnlineAtMount.current) {
      if (onRecover) onRecover();
      else window.location.reload();
    }
  }, [autoRecover, isOnline, onRecover]);

  const handleRetry = async () => {
    setIsChecking(true);
    setStillOffline(false);
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);
    try {
      // HEAD against our own origin: no CORS, no auth.
      await fetch(`${window.location.origin}/favicon.png`, {
        method: 'HEAD',
        cache: 'no-cache',
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
      if (onRecover) onRecover();
      else window.location.reload();
    } catch {
      clearTimeout(timeoutId);
      setIsChecking(false);
      setStillOffline(true);
    }
  };

  const isError = variant === 'error';
  const containerClass = fullScreen
    ? 'fixed inset-0 z-[99999] flex items-center justify-center bg-ds-bg-secondary'
    : inline
      ? 'flex items-center justify-center px-4 py-10'
      : 'min-h-screen flex items-center justify-center bg-ds-bg-secondary px-4';

  const badge = inline ? 'h-16 w-16' : 'mb-8 h-28 w-28';
  const glyph = inline ? 'h-8 w-8' : 'h-14 w-14';

  return (
    <div className={containerClass} role="alert" aria-live="assertive">
      <div className="flex w-full max-w-sm flex-col items-center px-6 text-center">
        <div
          className={`${badge} ${inline ? 'mb-4' : ''} flex shrink-0 items-center justify-center rounded-full bg-primary/10`}
        >
          <svg
            className={`${glyph} text-primary`}
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            aria-hidden="true"
          >
            {/* WiFi arcs, faded outward, struck through */}
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeOpacity={0.35}
              d="M1.5 8.25A15 15 0 0 1 22.5 8.25"
            />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeOpacity={0.6}
              d="M5.25 11.25a10.5 10.5 0 0 1 13.5 0"
            />
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 14.25a6 6 0 0 1 6 0" />
            <circle cx="12" cy="17.25" r="0.75" fill="currentColor" stroke="none" />
            <line x1="3" y1="3" x2="21" y2="21" strokeLinecap="round" strokeWidth={1.75} />
          </svg>
        </div>

        <p
          className={
            inline
              ? 'mb-1 text-base font-semibold text-ds-text-primary'
              : 'mb-3 text-2xl font-semibold leading-tight text-ds-text-primary'
          }
        >
          {isError ? t('couldNotLoadTitle') : t('noInternetConnection')}
        </p>

        <p className="text-sm leading-relaxed text-ds-text-tertiary">
          {isError ? t('couldNotLoadSubtitle') : t('offlineDescription')}
        </p>

        {stillOffline && (
          <p className="mt-2 text-xs font-medium text-error">{t('stillOfflineHint')}</p>
        )}

        <ActionButton
          label={isChecking ? t('checkingConnection') : t('tryAgain')}
          color="primary"
          size={inline ? 'sm' : 'lg'}
          onClick={() => void handleRetry()}
          disabled={isChecking}
          fullWidth={!inline}
          className={`shrink-0 ${inline ? 'mt-4' : 'mt-6'}`}
        />
      </div>
    </div>
  );
}
